import {
  CREATOR,
  ACCESS_TOKEN,
  AFTER_LOGIN_STORAGE_KEY,
} from '@libraries/auth';
import {
  privacyCollectionAndUsagePolicy,
  termsOfUse,
  thirdPartyPrivacyPolicy,
} from '@libraries/common/sitemap';

import type { SteadioTermsData } from '@steadioTypes/terms';
import type { UserProfile } from '@steadioTypes/user';
import type { AfterLogin } from '@steadioTypes/auth';

export const getSteadioTerms = (): SteadioTermsData[] => [
  {
    required: true,
    label: '만 14세 이상',
    value: 'above14yrsOld',
  },
  {
    required: true,
    label: '스테디오 이용약관',
    link: termsOfUse,
    value: 'termsOfUseAgreement',
  },
  {
    required: true,
    label: '개인정보 수집 및 이용',
    link: privacyCollectionAndUsagePolicy,
    value: 'privacyAgreement',
  },
  {
    required: false,
    label: '개인정보 제3자 제공 동의',
    link: thirdPartyPrivacyPolicy,
    value: 'thirdPartyPrivacyAgreement',
  },
  {
    required: false,
    label: '마케팅 정보 수신 동의',
    value: 'marketingAgreement',
  },
];

export const setAfterLogin = (afterLogin: AfterLogin) =>
  sessionStorage.setItem(AFTER_LOGIN_STORAGE_KEY, JSON.stringify(afterLogin));

export const getAfterLogin = (): AfterLogin => {
  const defaultValue: AfterLogin = {
    redirectTo: '/',
  };
  const afterLogin = JSON.parse(
    sessionStorage.getItem(AFTER_LOGIN_STORAGE_KEY) || 'null',
  );
  return afterLogin || defaultValue;
};

export const getUrlAfterSignIn = (userProfile: UserProfile): string => {
  if (!userProfile) {
    return '/login';
  }

  if (!userProfile.emailVerified) {
    return '/login/email-verification-guide';
  }

  const { redirectTo } = getAfterLogin();
  if (redirectTo !== CREATOR) {
    return redirectTo;
  }

  return getCreatorPageUrl({ userProfile, isSignedOut: false });
};

/**
 * GNB 크리에이터 시작하기 또는 `/start`페이지 크리에이터 계정만들기 버튼 클릭 시 이동 경로 리턴
 * @param { Object } - { userProfile, isSignedOut, isStartPage }
 * @return { string } -
 *   /login |
 *   /login/email-verification-guide |
 *   /creator-studio |
 *   /create-creator-account/required |
 *   /start
 */
export const getCreatorPageUrl = ({
  userProfile,
  isSignedOut,
  isStartPage,
}: {
  userProfile: UserProfile | undefined;
  isSignedOut: boolean;
  isStartPage?: boolean;
}) => {
  if (isSignedOut) {
    if (isStartPage) {
      setAfterLogin({ redirectTo: CREATOR });
      return '/login';
    }

    return '/start';
  }

  if (!userProfile?.emailVerified) {
    return '/login/email-verification-guide';
  }

  const startCreatorPath = isStartPage
    ? '/create-creator-account/required'
    : '/start'; // 크리에이터 온보딩 페이지

  return userProfile?.role === 'CREATOR' ? '/creator-studio' : startCreatorPath;
};

const creatorPageEntryPointStorageKey = 'creatorPageEntryPoint';

export const saveCreatorPageEntryPoint = (entryPointData: Object = {}) =>
  localStorage.setItem(
    creatorPageEntryPointStorageKey,
    JSON.stringify({
      pathname: location.pathname,
      ...entryPointData,
    }),
  );

export const getCreatorPageEntryPoint = () => {
  const entryPointData =
    localStorage.getItem(creatorPageEntryPointStorageKey) || '{}';
  try {
    return JSON.parse(entryPointData);
  } catch {
    return {};
  }
};

export const setAccessToken = (token: string) => {
  sessionStorage.setItem(ACCESS_TOKEN, token);
};

export const getAccessToken = (): string | null => {
  try {
    return sessionStorage.getItem(ACCESS_TOKEN);
  } catch {
    return null;
  }
};
